/**
 * バナーレイアウトの入口 (「背景・構図」の1フィールド・目立たせない) とエディタの置き場所。
 * docs/features/banner-layout-template-plan.md 方針・§7・§19。
 *
 * - 置き場所は属性ツリーの「レイアウト」ノード (features/bannerLayout/attributeNode.ts) が決める。CompositionSection が
 *   そのノードの位置 (sort_order / placement) にこの入口を描く。ラベルもノードから受け取る (パートナーの上書き込み)
 *
 * - 他のフィールド (F値など) と同じ「ラベル + トリガー」の見た目 (HierarchicalSelect.module.css を借りる)。
 *   トリガーには適用中のレイアウトのサムネイルと名前 (読み込んだプリセット / テンプレートの名前・白紙なら「カスタム」)、
 *   未設定なら「指定なし」。押すとエディタ。設定済みはラベル行の ✕ で外す
 * - 実効比率がレイアウトとずれたら「比率が変わりました」(生成ボタンは GenerationPreviewPanel が止める)
 */
import { useMemo } from 'react';
import { useBannerLayoutEditorStore } from '../../../features/bannerLayout/editorStore';
import { effectiveAspectRatio, ratiosMatch } from '../../../features/bannerLayout/effectiveAspectRatio';
import { reuseLayoutSettings } from '../../../features/bannerLayout/reuse';
import { parseRatio, type BannerLayout } from '../../../features/bannerLayout/types';
import { ExpandIcon } from '../../common/selectIcons';
import selectStyles from '../../common/HierarchicalSelect.module.css';
import { isStillImageUsableAsset } from '../state/assetMediaUtils';
import type { GenerationAsset, GenerationSettings } from '../state/types';
import { BannerLayoutCanvas } from './BannerLayoutCanvas';
import { BannerLayoutEditorModal } from './BannerLayoutEditorModal';
import type { InspectorAsset } from './BannerLayoutControls';
import styles from './BannerLayout.module.css';

type SetSettings = (next: GenerationSettings | ((prev: GenerationSettings) => GenerationSettings)) => void;

export function useBannerLayoutAssets(selectedAssets: GenerationAsset[]): InspectorAsset[] {
  return useMemo(
    () =>
      selectedAssets
        .filter(isStillImageUsableAsset)
        .filter((a) => !!a.thumbnailUrl)
        .map((a) => ({ id: a.id, thumbUrl: a.thumbnailUrl, name: a.name ?? undefined })),
    [selectedAssets],
  );
}

/** 比率が媒体規格・目標解像度で固定されているか */
export function isBannerLayoutRatioLocked(settings: GenerationSettings): boolean {
  return !!settings.media_spec_id || (!!settings.target_width && !!settings.target_height);
}

export function removeBannerLayout(setSettings: SetSettings) {
  // 文言は「テキスト」アコーディオンの欄にあるので、外すときに書き戻すものは無い (§13)
  setSettings((prev) => ({ ...prev, banner_layout: null, banner_layout_name: null }));
}

/** トリガー内のサムネイルの長辺 (高さを 24px に揃える) */
const ENTRY_THUMB_HEIGHT = 24;
function entryThumbSize(aspectRatio: string): number {
  const r = parseRatio(aspectRatio) ?? 1;
  return r >= 1 ? Math.round(ENTRY_THUMB_HEIGHT * r) : ENTRY_THUMB_HEIGHT;
}
// lucide の layout-dashboard (ISC・public/layout_dashboard.svg)。利用者選定 2026-10-18
const LAYOUT_ICON = '/layout_dashboard.svg';

export function BannerLayoutEntry({
  label = 'レイアウト',
  settings,
  setSettings,
  selectedAssets,
}: {
  /** 属性ツリーの「レイアウト」ノードのラベル (省略時は「レイアウト」) */
  label?: string;
  settings: GenerationSettings;
  setSettings: SetSettings;
  selectedAssets: GenerationAsset[];
}) {
  const open = useBannerLayoutEditorStore((s) => s.open);
  const assets = useBannerLayoutAssets(selectedAssets);
  const layout = settings.banner_layout;
  const effective = effectiveAspectRatio(settings);
  const mismatch = !!layout && !ratiosMatch(layout.aspectRatio, effective);
  const displayName = layout ? (settings.banner_layout_name || 'カスタム') : '指定なし';

  return (
    <div className={selectStyles.container} data-testid="banner-layout-entry">
      <div className={selectStyles.labelRow}>
        <div className={selectStyles.label}>
          <span
            className={selectStyles.labelIcon}
            style={{ maskImage: `url(${LAYOUT_ICON})`, WebkitMaskImage: `url(${LAYOUT_ICON})` }}
            aria-hidden="true"
          />
          {label}
        </div>
        {layout && (
          <button type="button" className={selectStyles.clearButton} onClick={() => removeBannerLayout(setSettings)} aria-label="レイアウトを外す" title="外す">
            ✕
          </button>
        )}
      </div>
      <button
        type="button"
        className={`${selectStyles.trigger} ${layout ? selectStyles.triggerHasValue : ''}`}
        onClick={open}
        aria-haspopup="dialog"
        title={layout ? `${displayName} (${layout.aspectRatio}・要素${layout.elements.length}個)` : undefined}
      >
        {layout && (
          <span className={styles.entryThumb} aria-hidden="true">
            <BannerLayoutCanvas aspectRatio={layout.aspectRatio} elements={layout.elements} background={layout.background} productImages={assets} size={entryThumbSize(layout.aspectRatio)} />
          </span>
        )}
        <span className={selectStyles.triggerText}>{displayName}</span>
        <span className={selectStyles.triggerArrow}><ExpandIcon /></span>
      </button>
      {mismatch && <span className={styles.entryWarn}>アスペクト比が変わりました。開いて適用し直してください</span>}
    </div>
  );
}

/** エディタモーダル (開閉は editorStore)。GenerationWorkspace に1つだけ置く */
export function BannerLayoutEditorHost({
  settings,
  setSettings,
  selectedAssets,
  logoBrandNames,
}: {
  settings: GenerationSettings;
  setSettings: SetSettings;
  selectedAssets: GenerationAsset[];
  logoBrandNames: string[];
}) {
  const isOpen = useBannerLayoutEditorStore((s) => s.isOpen);
  const close = useBannerLayoutEditorStore((s) => s.close);
  const assets = useBannerLayoutAssets(selectedAssets);
  if (!isOpen) return null;
  const ratioLocked = isBannerLayoutRatioLocked(settings);

  const apply = (layout: BannerLayout, name: string | null) => {
    // 生成のアスペクト比をレイアウトに合わせる。違う比率なら (エディタで確認済み) 比率を変え、媒体規格・解像度の指定を外す
    setSettings((prev) => ({ ...prev, ...reuseLayoutSettings(prev, layout), banner_layout_name: name }));
    close();
  };

  return (
    <BannerLayoutEditorModal
      initialLayout={settings.banner_layout ?? null}
      initialName={settings.banner_layout_name ?? null}
      attributes={settings.attributes}
      effectiveAspect={effectiveAspectRatio(settings)}
      ratioLocked={ratioLocked}
      assets={assets}
      logoBrandNames={logoBrandNames}
      excludeText={!!settings.exclude_text_response}
      onApply={apply}
      onClose={close}
    />
  );
}
